import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import type { Vehicle } from '../types';
import { CLASS_COLOURS, classLabel } from '../types';
import { vehicleImageSrc } from '../utils/helpers';
import StatusBadge from './StatusBadge';
import TagPill from './TagPill';

interface Props {
  vehicle: Vehicle;
  onClick: (v: Vehicle, el: HTMLDivElement) => void;
  selected?: boolean;
  selectable?: boolean;
  onSelect?: (id: number) => void;
}

export default function VehicleCard({ vehicle, onClick, selected, selectable, onSelect }: Props) {
  const cardRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!cardRef.current) return;
    const el = cardRef.current;
    const enter = () => gsap.to(el, { scale: 1.025, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', duration: 0.18, ease: 'power2.out' });
    const leave = () => gsap.to(el, { scale: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.12)', duration: 0.18, ease: 'power2.in' });
    el.addEventListener('mouseenter', enter);
    el.addEventListener('mouseleave', leave);
    return () => { el.removeEventListener('mouseenter', enter); el.removeEventListener('mouseleave', leave); };
  }, []);

  const imgUrl = vehicleImageSrc(vehicle);
  const displayName = vehicle.name ?? vehicle.spawncode;
  const classColour = CLASS_COLOURS[vehicle.vehicle_class as keyof typeof CLASS_COLOURS] ?? { bg: '#374151', text: '#FFFFFF' };

  const handleClick = () => {
    if (selectable) onSelect?.(vehicle.id);
    else onClick(vehicle, cardRef.current!);
  };

  return (
    <div
      ref={cardRef}
      className={`vehicle-card flex flex-col overflow-hidden relative ${selected ? 'ring-2 ring-primary' : ''}`}
      style={{ willChange: 'transform', cursor: selectable ? 'pointer' : undefined }}
      onClick={handleClick}
    >
      {/* Selection checkbox */}
      {selectable && (
        <div className="absolute top-2 left-2 z-10">
          <div
            style={{
              width: 18, height: 18,
              border: selected ? '2px solid #C8102E' : '2px solid var(--border-strong)',
              background: selected ? '#C8102E' : 'var(--card)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.13s, border-color 0.13s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
            }}
          >
            {selected && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <polyline points="1.5,5 4,7.5 8.5,2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Selected overlay tint */}
      {selected && (
        <div className="absolute inset-0 z-[5] pointer-events-none" style={{ background: 'rgba(200,16,46,0.06)' }} />
      )}

      {/* Image */}
      <div
        className="h-24 flex items-center justify-center overflow-hidden relative"
        style={{ backgroundColor: classColour.bg + '18' }}
      >
        {imgUrl ? (
          <img src={imgUrl} alt={displayName} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="flex flex-col items-center gap-1" style={{ color: classColour.bg }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              <rect x="2" y="7" width="20" height="12" rx="0"/>
              <path d="M16 7l-2-4H10L8 7"/>
              <circle cx="7" cy="16" r="2"/>
              <circle cx="17" cy="16" r="2"/>
            </svg>
            <span className="text-[9px] font-body uppercase tracking-wide opacity-60">No Image</span>
          </div>
        )}
      </div>

      {/* Class badge — full width, colored */}
      <div
        className="text-center py-1"
        style={{ backgroundColor: classColour.bg, color: classColour.text }}
      >
        <span className="font-heading text-[11px] uppercase tracking-widest font-bold">
          {classLabel(vehicle.vehicle_class)}
        </span>
      </div>

      {/* Centered info block */}
      <div className="flex flex-col items-center justify-center gap-1.5 px-2 py-2 text-center flex-1">
        {/* Name */}
        <p className="font-heading text-sm font-bold leading-tight line-clamp-2 w-full" style={{ color: 'var(--text)' }}>
          {displayName}
        </p>

        {/* Spawncode */}
        <p className="font-body text-[10px] w-full truncate" style={{ color: 'var(--text-muted)' }}>
          {vehicle.spawncode}
        </p>

        {/* Status */}
        <StatusBadge status={vehicle.progress} small />

        {/* Flags */}
        {(vehicle.boosting_exclusive === 1 || vehicle.rp_exclusive === 1) && (
          <div className="flex gap-1 flex-wrap justify-center">
            {vehicle.boosting_exclusive === 1 && (
              <span className="text-[9px] font-body border border-gold text-gold px-1 py-0 pill uppercase">Boost</span>
            )}
            {vehicle.rp_exclusive === 1 && (
              <span className="text-[9px] font-body border border-primary text-primary px-1 py-0 pill uppercase">RP</span>
            )}
          </div>
        )}

        {/* Tags */}
        {vehicle.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 justify-center mt-auto">
            {vehicle.tags.slice(0, 2).map(t => <TagPill key={t.id} tag={t} small />)}
            {vehicle.tags.length > 2 && (
              <span className="text-[9px] text-text-muted">+{vehicle.tags.length - 2}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
